/* eslint-disable react/prop-types */
import React, { createContext, useContext, useMemo, useReducer } from "react";
import useDebouncedDispatch from "../../_hook/useDebouncedDispatch";

export const ActionType = {
  SET_ERROR: "SET_ERROR",
  SET_LOADING: "SET_LOADING",
  SET_NAV_HEIGHT: "SET_NAV_HEIGHT",
  SET_VISIBILITY: "SET_VISIBILITY",
  SET_FOOTER_HOME: "SET_FOOTER_HOME",
  SET_DRAWER_STATE: "SET_DRAWER_STATE",
};


// Centralized initial state for all context states
const initialState = {
  error: null,
  isError: false,
  isLoading: false,
  navHeight: 0,
  visibility: {
    showAppBar: true,
    showMenuIcon: true,
    isFixed:false
  },
  isFooterHome: false,
  drawerStates: {
    isLeftMenuOpen: false,
    isFilterDrawer: false,
    isExpanded:true
  },
};

export const layoutContext = createContext();

export function useLayout() {
  const value = useContext(layoutContext);
  if (!value) {
    throw new Error("useLayout must be used within a DefaultProvider");
  }
  if (!value && process.env.NODE_ENV !== "production") {
    throw new Error(
      "[layout]: `useLayout` must be wrapped in a <DefaultProvider />"
    );
  }
  return value;
}

const layoutReducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_ERROR:
      return { ...state, error: action.payload, isError: !!action.payload };
    case ActionType.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ActionType.SET_NAV_HEIGHT:
      return { ...state, navHeight: action.payload };
    case ActionType.SET_VISIBILITY:
      return {
        ...state,
        visibility: { ...state.visibility, ...action.payload },
      };
    case ActionType.SET_FOOTER_HOME:
      return { ...state, isFooterHome: action.payload };
    case ActionType.SET_DRAWER_STATE:
      return {
        ...state,
        drawerStates: { ...state.drawerStates, ...action.payload },
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export default function DefaultProvider({ children }) {
  const [state, dispatch] = useReducer(layoutReducer, initialState);

    // Generic dispatch handler function
    const handleDispatch = useDebouncedDispatch(dispatch, 300);


  // Drawer control functions
  const handleOpenDrawer = (drawer) => {
    handleDispatch(ActionType.SET_DRAWER_STATE, { [drawer]: true });
  };

  const handleCloseDrawer = (drawer) => {
    handleDispatch(ActionType.SET_DRAWER_STATE, { [drawer]: false });
  };
  const handleOpenDrawerDefault = (drawer) => {
    handleDispatch(ActionType.SET_DRAWER_STATE, { ...initialState?.drawerStates,[drawer]: true});
  };

  // Actions object with functions for setting specific states
  const layoutActions = {
    setError: (payload) => handleDispatch(ActionType.SET_ERROR, payload),
    setLoading: (payload) => handleDispatch(ActionType.SET_LOADING, payload),
    setNavHeight: (payload) => handleDispatch(ActionType.SET_NAV_HEIGHT, payload),
    setVisibility: (payload) => handleDispatch(ActionType.SET_VISIBILITY, payload),
    setFooterHome: (payload) => handleDispatch(ActionType.SET_FOOTER_HOME, payload),
    handleOpenDrawerDefault,
    handleOpenDrawer,
    handleCloseDrawer,
  };

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
      ...layoutActions, // Spread actions to make them directly accessible
    }),
    [state]
  );

  return (
    <layoutContext.Provider value={value}>{children}</layoutContext.Provider>
  );
}